"use client"

import { useEffect, useState } from "react"
import { AppShell, Badge, Box, Group, Button, Image, Burger, Stack, Loader, Skeleton, useDisclosure, useMediaQuery } from "@forestsync/ui"
import { ThemeSwitcher, LanguageSelector, CountryFlag } from "@forestsync/ui"
import { defaultLocale, locales } from "@forestsync/i18n"
import { setUserLocale } from "@forestsync/i18n/actions"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { TbChartBar, TbLayoutDashboard, TbArrowRight, TbExternalLink } from "react-icons/tb"

import { SidebarContext } from "@/components/layout/SidebarContext"
import { UserMenu } from "@/components/layout/UserMenu"
import { useAuth } from "@/components/layout/AuthProvider"
import { UiModeToggle } from "@/components/ui-mode"
import { useUiMode } from "@/components/ui-mode/UiModeProvider"
import { Footer } from "@/components/layout/Footer"
import { MarketTree } from "@/components/trading/MarketTree"
import { DashboardNav } from "@/components/dashboard/DashboardNav"
import { TraderBalanceChip } from "@/components/layout/TraderBalanceChip"
import { FeedbackButton } from "@/components/layout/FeedbackButton"

const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100"

interface MarketplaceShellProps {
  children: React.ReactNode
}

export function MarketplaceShell({ children }: MarketplaceShellProps) {
  const { userEmail, orgRole, orgLogoUrl, stripeOnboardingComplete, stripeRestricted, markets, tradingEnabled, viewOnly, isLoading } = useAuth()
  const t = useTranslations("nav")
  const { mode: uiMode } = useUiMode()
  const [currentLocale, setCurrentLocale] = useState(defaultLocale)
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false)
  const [sidebarCollapsed, { toggle: toggleSidebar, open: openSidebar }] = useDisclosure(false)
  const isDesktop = useMediaQuery("(min-width: 62em)")

  useEffect(() => {
    if (localStorage.getItem("sidebar-collapsed") === "true") openSidebar()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(sidebarCollapsed))
  }, [sidebarCollapsed])

  const pathname = usePathname()
  const isGuest = !userEmail
  const isTrader = !isGuest && uiMode === "trader"
  const isMarket = pathname.startsWith("/market")
  const isDashboard = pathname.startsWith("/dashboard")
  const isMarketTrader = isTrader && isMarket
  const showSidebar = !isGuest && (isDashboard || (isMarketTrader && !!isDesktop))
  const defaultMarketHref = isTrader && markets[0]
    ? `/market?creditType=${encodeURIComponent(markets[0].creditType)}&vintage=${markets[0].vintage}&projectRefId=${encodeURIComponent(markets[0].projectRefId)}`
    : "/market"

  const handleLocaleChange = async (locale: string | null) => {
    if (locale) {
      await setUserLocale(locale)
      setCurrentLocale(locale)
    }
  }

  const localesWithFlags = locales.map((locale) => ({
    ...locale,
    flag: locale.countryCode ? (
      <CountryFlag countryCode={locale.countryCode} width={16} height={12} className="inline-block" />
    ) : undefined,
  }))

  // View-only accounts (observers, merchant dashboards) can't trade, but they can still switch
  // between the catalog and the read-only terminal like everyone else.
  const showTraderControls = !isGuest && (tradingEnabled || !!stripeOnboardingComplete || viewOnly)
  const showBalanceChip = !isGuest

  return (
    <SidebarContext.Provider value={{ collapsed: sidebarCollapsed, toggle: toggleSidebar }}>
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 280,
        breakpoint: "sm",
        collapsed: { desktop: sidebarCollapsed || !showSidebar, mobile: !mobileOpened },
      }}
      footer={{ height: 40, collapsed: isMarketTrader }}
      padding={0}
    >
      <AppShell.Header>
        <Box h="100%" px="md" className="grid items-center grid-cols-[1fr_auto_1fr]">
          {/* Logo — left column */}
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <Link href="/" scroll>
              <Image
                src="/logo.svg"
                alt="Carbonomex"
                h={40}
                w="auto"
                fallbackSrc="/logo.png"
              />
            </Link>
            <Badge
              size="xs"
              variant="gradient"
              gradient={{ from: "forest", to: "teal" }}
              c="var(--mantine-color-body)"
              mb={4}
            >
              BETA
            </Badge>
          </Group>

          {/* Desktop nav links — center column */}
          <Group gap="xs" visibleFrom="sm">
            <Button
              variant="subtle"
              size="sm"
              component={Link}
              href={defaultMarketHref}
              leftSection={<TbChartBar size={16} />}
            >
              {t("market")}
            </Button>
            {!isGuest && (
              <Button
                variant="subtle"
                size="sm"
                component={Link}
                href="/dashboard"
                leftSection={<TbLayoutDashboard size={16} />}
              >
                {t("dashboard")}
              </Button>
            )}
            <Button
              variant="subtle"
              size="sm"
              component="a"
              href={REGISTRY_URL}
              target="_blank"
              rel="noopener noreferrer"
              rightSection={<TbExternalLink size={13} />}
            >
              {t("registry")}
            </Button>
          </Group>

          {/* Right side controls — right column */}
          <Group gap="xs" wrap="nowrap" justify="flex-end">
            {showTraderControls && (
              <Box visibleFrom="md">
                <UiModeToggle />
              </Box>
            )}
            {showBalanceChip && (
              <Box visibleFrom="md">
                <TraderBalanceChip />
              </Box>
            )}
            <Box visibleFrom="sm">
              <FeedbackButton />
            </Box>
            <ThemeSwitcher size="md" variant="subtle" showSystemOption={false} />
            <LanguageSelector
              locales={localesWithFlags}
              currentLocale={currentLocale}
              onLocaleChange={handleLocaleChange}
              displayVariant="icon"
              size="md"
              variant="subtle"
            />
            {isLoading ? (
              <Loader size="xs" />
            ) : userEmail ? (
              <UserMenu email={userEmail} logoUrl={orgLogoUrl} />
            ) : (
              <>
                <Button size="sm" variant="subtle" component={Link} href="/login" visibleFrom="sm">
                  {t("signIn")}
                </Button>
                <Button
                  size="sm"
                  radius="xl"
                  color="forest"
                  autoContrast
                  component={Link}
                  href="/register"
                  rightSection={<TbArrowRight size={16} />}
                  visibleFrom="sm"
                >
                  {t("createAccount")}
                </Button>
              </>
            )}
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
          </Group>
        </Box>
      </AppShell.Header>

      <AppShell.Navbar p={0}>
        {/* Desktop sidebar */}
        {isDashboard && (
          <Box visibleFrom="sm" h="100%">
            <DashboardNav
              orgRole={orgRole}
              stripeOnboardingComplete={stripeOnboardingComplete}
              stripeRestricted={stripeRestricted}
              tradingEnabled={tradingEnabled}
              viewOnly={viewOnly}
              defaultMarketHref={defaultMarketHref}
            />
          </Box>
        )}
        {isMarketTrader && (
          <Box visibleFrom="md" h="100%">
            {isLoading ? (
              <Stack p="md" gap="xs">
                <Skeleton height={32} radius="sm" />
                <Skeleton height={24} radius="sm" />
                <Skeleton height={24} radius="sm" />
                <Skeleton height={24} radius="sm" />
              </Stack>
            ) : (
              <MarketTree markets={markets} />
            )}
          </Box>
        )}

        {/* Mobile nav */}
        <Stack gap="sm" hiddenFrom="sm" p="md">
          <Button
            variant="subtle"
            size="sm"
            component={Link}
            href={defaultMarketHref}
            onClick={closeMobile}
            justify="flex-start"
            fullWidth
            leftSection={<TbChartBar size={16} />}
          >
            {t("market")}
          </Button>
          {!isGuest && (
            <Button
              variant="subtle"
              size="sm"
              component={Link}
              href="/dashboard"
              onClick={closeMobile}
              justify="flex-start"
              fullWidth
              leftSection={<TbLayoutDashboard size={16} />}
            >
              {t("dashboard")}
            </Button>
          )}
          <Button
            variant="subtle"
            size="sm"
            component="a"
            href={REGISTRY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMobile}
            justify="flex-start"
            fullWidth
            rightSection={<TbExternalLink size={13} />}
          >
            {t("registry")}
          </Button>
          {showTraderControls && <UiModeToggle />}
          <FeedbackButton variant="menuItem" />
          <Group gap="xs">
            <ThemeSwitcher size="md" variant="subtle" showSystemOption={false} />
            <LanguageSelector
              locales={localesWithFlags}
              currentLocale={currentLocale}
              onLocaleChange={handleLocaleChange}
              displayVariant="icon"
              size="md"
              variant="subtle"
            />
          </Group>
          {isGuest && !isLoading && (
            <>
              <Button size="sm" variant="subtle" component={Link} href="/login" onClick={closeMobile} fullWidth>
                {t("signIn")}
              </Button>
              <Button
                size="sm"
                radius="xl"
                color="forest"
                autoContrast
                component={Link}
                href="/register"
                onClick={closeMobile}
                rightSection={<TbArrowRight size={16} />}
                fullWidth
              >
                {t("createAccount")}
              </Button>
            </>
          )}
          {isDashboard && (
            <DashboardNav
              onNavigate={closeMobile}
              orgRole={orgRole}
              stripeOnboardingComplete={stripeOnboardingComplete}
              stripeRestricted={stripeRestricted}
              tradingEnabled={tradingEnabled}
              viewOnly={viewOnly}
              defaultMarketHref={defaultMarketHref}
            />
          )}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        {children}
      </AppShell.Main>

      {!isMarketTrader && (
        <AppShell.Footer>
          <Footer />
        </AppShell.Footer>
      )}
    </AppShell>
    </SidebarContext.Provider>
  )
}
